/**
 * Async Form Handling
 * Automatische Verarbeitung von Forms mit data-async Attribut
 */
import { safeSelect } from './utils.js';

function reloadCaptcha(captchaEl) {
	const img = captchaEl?.querySelector('.form__captcha-img');
	const input = captchaEl?.querySelector('input');
	if (img) {
		img.src = img.src.split('?')[0] + '?t=' + new Date().getTime();
	}
	if (input) {
		input.value = '';
	}
}

const formCallbacks = {
	form_success: (form, result) => handleFormSuccess(form, result),
	formUpdated: (form) => {
		const contentDiv = safeSelect('.form__content', form);
		const messagesDiv = safeSelect('.form__messages', form);
		if (contentDiv) contentDiv.style.display = 'none';
		if (messagesDiv) {
			messagesDiv.style.display = 'block';
			messagesDiv.classList.add('form__messages--success');
		}
	},
};

function initCharCounter(textarea) {
	const max = parseInt(textarea.getAttribute('maxlength'), 10);
	if (!max) return;
	const counter = document.createElement('span');
	counter.className = 'form__char-counter';
	counter.textContent = `0 / ${max}`;
	textarea.after(counter);
	textarea.addEventListener('input', function() {
		counter.textContent = `${textarea.value.length} / ${max}`;
	});
}

/**
 * Initialisiert Async Form-Handling
 */
export const initAsyncForms = function() {
	try {
		const forms = document.querySelectorAll('form[data-async]');

		if (forms.length === 0) {
			console.log('Keine async Forms gefunden');
			return;
		}

		forms.forEach(function(form) {
			form.addEventListener('submit', function(e) {
				e.preventDefault();
				handleFormSubmit(form);
			});
		});

		document.querySelectorAll('textarea[maxlength]').forEach(initCharCounter);

		document.querySelectorAll('.form__captcha-reload').forEach(function(btn) {
			btn.addEventListener('click', function(e) {
				e.preventDefault();
				e.stopPropagation();
				reloadCaptcha(btn.closest('.form__captcha'));
			});
		});

		console.log(`${forms.length} async Form(s) initialisiert`);
	} catch (error) {
		console.error('Fehler bei der Initialisierung von Async Forms:', error);
	}
};

/**
 * Behandelt Form-Submission
 */
async function handleFormSubmit(form) {
	const submitButton = safeSelect('button[type="submit"]', form);

	// Button deaktivieren während Submission
	if (submitButton) {
		submitButton.disabled = true;
		submitButton.classList.add('is-loading');
	}

	// Fehler zurücksetzen
	clearFormErrors(form);

	const enctype = (form.enctype || "").toLowerCase();
    const action = form.action;

    // Always build FormData from the form:
    const formData = new FormData(form);
    let payload = { method: "POST" };

    if (enctype === "multipart/form-data") {
    	// Use multipart endpoint (fhm) for file uploads
    	payload.body = formData;
    } else {
    	const urlEncoded = new URLSearchParams(formData);
    	payload.headers = {
    		"Content-Type": "application/x-www-form-urlencoded"
    	};
    	payload.body = urlEncoded.toString();
    }

	try {
		const response = await fetch(action, payload);
		const result = await response.json();

		if (result.callme && formCallbacks[result.callme]) {
			formCallbacks[result.callme](form, result);
		} else if(result.redir) {
			window.location.reload();
		} else if (result.errors) {
			// Validierungsfehler
			handleFormErrors(form, result.errors);
		} else if (result.error) {
			// Genereller Fehler
			showFormMessage(form, 'error', 'Ein Fehler ist aufgetreten: ' + result.error);
		}
	} catch (error) {
		console.error('Form submission error:', error);
		showFormMessage(form, 'error', 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
	} finally {
		// Button wieder aktivieren
		if (submitButton) {
			submitButton.disabled = false;
			submitButton.classList.remove('is-loading');
		}
	}
}

/**
 * Behandelt erfolgreiche Form-Submission
 */
function handleFormSuccess(form, result) {
	// Comment-Form: Neues Kommentar einfügen, Form zurücksetzen (kein Reload)
	if (form.id === 'comment-form' && result.result && result.result.name) {
		appendNewComment(form, result.result);
		return;
	}

	const contentDiv = safeSelect('.form__content', form);
	const messagesDiv = safeSelect('.form__messages', form);

	// Form-Content ausblenden
	if (contentDiv) {
		contentDiv.style.display = 'none';
	}

	// Success-Message anzeigen
	if (messagesDiv) {
		messagesDiv.style.display = 'block';
		messagesDiv.classList.add('form__messages--success');
	}

	// Event für externe Handler
	const event = new CustomEvent('formSuccess', {
		detail: {
			type: result.type,
			result: result.result,
			form: form
		}
	});
	document.dispatchEvent(event);

	// Optional: Seite neu laden nach kurzer Verzögerung
	if (form.dataset.reloadOnSuccess === 'true') {
		setTimeout(function() {
			location.reload();
		}, 2000);
	}
}

/**
 * Fügt neues Kommentar zur Liste hinzu und setzt Formular zurück
 */
function appendNewComment(form, comment) {
	const listEl = safeSelect('.comments__list', form.closest('.comments'));
	if (!listEl) return;

	// "Keine Kommentare"-Hinweis entfernen
	const noticeEl = safeSelect('.comment__notice', listEl);
	if (noticeEl) {
		noticeEl.remove();
	}

	const userpicHtml = comment.author_image
		? `<img src="${escapeHtml(comment.author_image)}" alt="" class="comment__userpic-image" width="40" height="40"/>`
		: '<span class="comment__userpic-placeholder" aria-hidden="true"></span>';

	const commentEl = document.createElement('article');
	commentEl.className = 'comment comments__item';
	commentEl.innerHTML = `
		<header class="comment__header">
			<div class="comment__meta">
				<div class="comment__userpic">${userpicHtml}</div>
				<span class="comment__author">${escapeHtml(comment.name)}</span>
				<time class="comment__date" datetime="${escapeHtml(comment.dateIso || comment.date || '')}">${escapeHtml(comment.date || '')}</time>
			</div>
		</header>
		<div class="comment__content">
			<p>${escapeHtml(comment.text)}</p>
		</div>
	`;

	listEl.insertBefore(commentEl, listEl.firstChild);

	// Formular zurücksetzen
	form.reset();

	// Optional: kurze Success-Meldung anzeigen
	const messagesDiv = safeSelect('.form__messages', form);
	if (messagesDiv) {
		messagesDiv.style.display = 'block';
		messagesDiv.classList.add('form__messages--success');
		setTimeout(function() {
			messagesDiv.style.display = 'none';
			messagesDiv.classList.remove('form__messages--success');
		}, 3000);
	}
}

function escapeHtml(str) {
	if (str == null) return '';
	const div = document.createElement('div');
	div.textContent = str;
	return div.innerHTML;
}

/**
 * Behandelt Validierungsfehler
 */
function handleFormErrors(form, errors) {
	for (const fieldName in errors) {
		if (errors.hasOwnProperty(fieldName)) {
            // Über data-formfield statt #field-<name> suchen: die ID kann je nach
            // Formular-Instanz einen Suffix tragen (z.B. field-email-modal), data-formfield
            // bleibt unsuffixiert und identifiziert das Feld eindeutig innerhalb des Formulars.
            const fieldGroup = form.querySelector('[data-formfield="' + fieldName + '"]');

			if (fieldGroup) {
				fieldGroup.classList.add('form__group--error');
				const errorDiv = document.createElement('div');
				errorDiv.className = 'form__error';
				errorDiv.textContent = errors[fieldName];
				fieldGroup.appendChild(errorDiv);
				const input = safeSelect('input, textarea, select', fieldGroup);

				if (input) {
					const errorId = 'error-' + (form.id || 'form') + '-' + fieldName;
					input.classList.add('form__input--error');
					input.setAttribute('aria-invalid', 'true');
					input.setAttribute('aria-describedby', errorId);
					errorDiv.id = errorId;
				}
			}
		}
	}

	// Captcha nach Fehler erneuern
	const captchaEl = form.querySelector('.form__captcha');
	if (captchaEl) {
		reloadCaptcha(captchaEl);
	}

	// Zum ersten Fehler scrollen
	const firstError = safeSelect('.form__group--error', form);
	if (firstError) {
		firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}
}

/**
 * Löscht alle Fehler aus dem Formular
 */
function clearFormErrors(form) {
	// Error-Messages entfernen
	const errorMessages = form.querySelectorAll('.form__error');
	errorMessages.forEach(function(el) {
		el.remove();
	});

	// Error-Klassen entfernen
	const errorGroups = form.querySelectorAll('.form__group--error');
	errorGroups.forEach(function(el) {
		el.classList.remove('form__group--error');
	});

	const errorInputs = form.querySelectorAll('.form__input--error');
	errorInputs.forEach(function(el) {
		el.classList.remove('form__input--error');
		el.removeAttribute('aria-invalid');
		el.removeAttribute('aria-describedby');
	});
}

/**
 * Zeigt eine allgemeine Nachricht im Formular an
 */
function showFormMessage(form, type, message) {
	let messageDiv = safeSelect('.form__message--' + type, form);

	if (!messageDiv) {
		messageDiv = document.createElement('div');
		messageDiv.className = 'form__message form__message--' + type;
		form.insertBefore(messageDiv, form.firstChild);
	}

	messageDiv.textContent = message;
	messageDiv.style.display = 'block';
}
